import { useQuery, useQueryClient, type QueryClient, type UseQueryResult } from '@tanstack/react-query';
import type {
  AddToCvBankInput,
  CandidateCvBankView,
  CvBankAddResult,
  CvBankDuplicatePair,
  CvBankEntryView,
  CvBankSource,
  CvBankSuggestion,
  CvBankUploadRequest,
  CvBankUploadResult,
  RemoveFromCvBankInput,
  RemoveMatchingFromCvBankRequest,
  ResolveCvBankDuplicateInput,
} from '@nest-recruit/shared';
import { ApiError, apiGet, apiPost, request } from '../../../lib/api-client';

// ADR 0046 — the CV Bank. Keys sit under `['candidates']`, so the API's candidate hints and its
// own `['candidates', 'cv-bank']` hint refresh every open bank, profile card and duplicate list.

export const CV_BANK_KEY = ['candidates', 'cv-bank'] as const;
const DUPLICATES_KEY = [...CV_BANK_KEY, 'duplicates'] as const;

export function cvBankListKey(params: CvBankListParams): readonly unknown[] {
  return [...CV_BANK_KEY, 'list', params];
}

export function candidateCvBankKey(candidateId: string): readonly unknown[] {
  return [...CV_BANK_KEY, 'candidate', candidateId];
}

export interface CvBankListParams {
  readonly page: number;
  readonly pageSize: number;
  readonly query?: string;
  readonly source?: CvBankSource;
}

export interface CvBankPage {
  readonly rows: readonly CvBankEntryView[];
  readonly total: number;
}

export function useCvBank(params: CvBankListParams): UseQueryResult<CvBankPage, ApiError> {
  return useQuery<CvBankPage, ApiError>({
    queryKey: cvBankListKey(params),
    queryFn: async () => {
      const result = await request<readonly CvBankEntryView[]>('/cv-bank', { query: { ...params } });
      return { rows: result.data, total: result.meta?.total ?? result.data.length };
    },
    // Never blank a list the viewer can already see (CLAUDE.md §5a rule 2).
    placeholderData: (previous) => previous,
  });
}

export function useCvBankDuplicates(isEnabled: boolean = true): UseQueryResult<readonly CvBankDuplicatePair[], ApiError> {
  return useQuery<readonly CvBankDuplicatePair[], ApiError>({
    queryKey: DUPLICATES_KEY,
    queryFn: async () => (await apiGet<{ pairs: readonly CvBankDuplicatePair[] }>('/cv-bank/duplicates')).pairs,
    enabled: isEnabled,
    placeholderData: (previous) => previous,
  });
}

/** A link to an entry that is not on the page in view (`?entry=`): it is fetched on its own. */
export function useCvBankEntry(entryId: string | null): UseQueryResult<CvBankEntryView, ApiError> {
  return useQuery<CvBankEntryView, ApiError>({
    queryKey: [...CV_BANK_KEY, 'entry', entryId],
    queryFn: () => apiGet<CvBankEntryView>(`/cv-bank/entries/${entryId ?? ''}`),
    enabled: entryId !== null,
    retry: false,
  });
}

export function useCandidateCvBank(candidateId: string, isEnabled: boolean): UseQueryResult<CandidateCvBankView, ApiError> {
  return useQuery<CandidateCvBankView, ApiError>({
    queryKey: candidateCvBankKey(candidateId),
    queryFn: () => apiGet<CandidateCvBankView>(`/cv-bank/candidates/${candidateId}`),
    enabled: isEnabled,
  });
}

export const cvBankApi = {
  add: (input: AddToCvBankInput): Promise<CvBankAddResult> => apiPost<CvBankAddResult>('/cv-bank/entries', input),
  remove: (input: RemoveFromCvBankInput): Promise<{ readonly removedEntryIds: readonly string[] }> => apiPost('/cv-bank/entries/remove', input),
  removeMatching: (input: RemoveMatchingFromCvBankRequest): Promise<{ readonly removedEntryIds: readonly string[] }> => apiPost('/cv-bank/entries/remove-matching', input),
  restore: (entryIds: readonly string[]): Promise<{ readonly restoredEntryIds: readonly string[] }> => apiPost('/cv-bank/entries/restore', { entryIds }),
  resolve: (input: ResolveCvBankDuplicateInput): Promise<{ readonly removedEntryIds: readonly string[] }> => apiPost('/cv-bank/duplicates/resolve', input),
  suggest: async (file: File): Promise<CvBankSuggestion> => {
    const body = new FormData();
    body.append('cv', file);
    return (await request<CvBankSuggestion>('/cv-bank/suggest', { method: 'POST', body })).data;
  },
  upload: async (file: File, fields: CvBankUploadRequest): Promise<CvBankUploadResult> => {
    const body = new FormData();
    body.append('fullName', fields.fullName);
    body.append('email', fields.email ?? '');
    body.append('phone', fields.phone ?? '');
    body.append('hasConsent', String(fields.hasConsent));
    body.append('cv', file);
    return (await request<CvBankUploadResult>('/cv-bank/upload', { method: 'POST', body })).data;
  },
};

/** After a change lands: the bank, its duplicates, the profile cards and the retention line. */
export function refreshCvBank(queryClient: QueryClient): Promise<void> {
  return Promise.all([queryClient.invalidateQueries({ queryKey: CV_BANK_KEY }), queryClient.invalidateQueries({ queryKey: ['candidates', 'detail'] })]).then(() => undefined);
}

export function useRefreshCvBank(): () => Promise<void> {
  const queryClient = useQueryClient();
  return () => refreshCvBank(queryClient);
}

/** §5a rule 1: the rows leave every cached page at once; the returned function puts them back. */
export function dropFromLists(queryClient: QueryClient, entryIds: ReadonlySet<string>): () => void {
  const before = queryClient.getQueriesData<CvBankPage>({ queryKey: [...CV_BANK_KEY, 'list'] });
  for (const [key, page] of before) {
    if (!page) continue;
    const rows = page.rows.filter((row) => !entryIds.has(row.id));
    queryClient.setQueryData<CvBankPage>(key, { rows, total: page.total - (page.rows.length - rows.length) });
  }
  return () => {
    for (const [key, page] of before) queryClient.setQueryData(key, page);
  };
}

/** The same for a duplicate pair the user just settled. */
export function dropPair(queryClient: QueryClient, pair: CvBankDuplicatePair): () => void {
  const before = queryClient.getQueryData<readonly CvBankDuplicatePair[]>(DUPLICATES_KEY);
  if (before) queryClient.setQueryData<readonly CvBankDuplicatePair[]>(DUPLICATES_KEY, before.filter((other) => !(other.first.id === pair.first.id && other.second.id === pair.second.id)));
  return () => {
    if (before) queryClient.setQueryData(DUPLICATES_KEY, before);
  };
}
